/**
 * Weekly archive of cards sitting in a Complete column.
 *
 * Jeremy asked (31 Oct 2026) for the sundaystate Team board: once a week,
 * Sunday night, take cards in Complete off the board. Archive, never delete.
 *
 * Scope is opt-in, and only a column whose kind is "complete":
 *   - The Team board (5a7d0000-7ea3-4b0a-9d00-000000000001) is the one he
 *     named. The migration turns auto_archive_done on for that board only.
 *   - The other board is the Wholesale Sales Pipeline. Its Closed Won and
 *     Closed Lost columns are is_done, but their kind is null. Archiving
 *     every done column would hide that pipeline. kind = 'complete' does not
 *     match them, even if somebody later turns the flag on by mistake.
 *
 * A card is archived by setting hq_tasks.archived_at. The row, its comments
 * and its history stay. The activity trigger writes the log row.
 *
 * Delete in the app is the same stamp, with reason 'deleted'. A permanent
 * delete is only for hq_employees.role = 'super_admin' (isSuperAdmin), and
 * only after the card is archived.
 */

import { isSuperAdmin } from "@/lib/permissions";
import type { Role } from "@/types/db";

export const TEAM_BOARD_ID = "5a7d0000-7ea3-4b0a-9d00-000000000001";

export interface ArchiveBoard {
  id: string;
  auto_archive_done: boolean;
  /** The whole board is retired. Distinct from a card being archived. */
  archived?: boolean | null;
}

export interface ArchiveColumn {
  id: string;
  board_id: string;
  is_done?: boolean | null;
  kind: string | null;
}

export interface ArchiveCard {
  id: string;
  board_id: string;
  column_id: string;
  archived_at?: string | null;
}

export type ArchiveReason = "deleted" | "auto_complete";

export function isArchived(card: { archived_at?: string | null }): boolean {
  return card.archived_at != null && card.archived_at !== "";
}

/** Cleared together so a card put back is not still marked deleted. */
export const UNARCHIVE_PATCH = {
  archived_at: null,
  archived_by: null,
  archive_reason: null,
} as const;

/** What the Delete button writes. The row stays; reason marks it as a person's delete. */
export function personDeletePatch(employeeId: string, now: string) {
  return {
    archived_at: now,
    archived_by: employeeId,
    archive_reason: "deleted" as const,
  };
}

/**
 * Permanent removal. The role is hq_employees.role, not a permission key.
 * 'admin' is not enough, and the card must already be archived.
 */
export function canPermanentlyDelete(
  role: Role | string | null | undefined,
  card: { archived_at?: string | null },
): boolean {
  return isSuperAdmin(role as Role | null | undefined) && isArchived(card);
}

/**
 * Cards the Sunday job should stamp. Already-archived cards are left alone,
 * so a second run the same night changes nothing.
 */
export function cardsToArchive(boards: ArchiveBoard[], columns: ArchiveColumn[], cards: ArchiveCard[]): ArchiveCard[] {
  const liveBoards = new Set(
    boards.filter(b => b.auto_archive_done && !b.archived).map(b => b.id),
  );
  const completeColumns = new Set(
    columns.filter(c => liveBoards.has(c.board_id) && c.kind === "complete").map(c => c.id),
  );
  const seen = new Set<string>();
  const out: ArchiveCard[] = [];
  for (const card of cards) {
    if (isArchived(card)) continue;
    if (!liveBoards.has(card.board_id)) continue;
    if (!completeColumns.has(card.column_id)) continue;
    if (seen.has(card.id)) continue;
    seen.add(card.id);
    out.push(card);
  }
  return out;
}

/**
 * Moving an archived card out of Complete puts it back on the board.
 * Leaving it in Complete keeps it archived until somebody asks.
 */
export function shouldUnarchiveOnMove(
  card: { archived_at?: string | null },
  dest: { kind?: string | null } | undefined,
): boolean {
  return isArchived(card) && !!dest && dest.kind !== "complete";
}

export function chunk<T>(items: T[], size: number): T[][] {
  if (size < 1) throw new Error("chunk size must be at least 1");
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * A manual HTTP trigger is allowed for an administrator, or for the
 * scheduler secret / the platform schedule (those are not "employee").
 * Any other signed-in person is refused: this job archives a whole column.
 */
export function manualRunAllowed(gate: { via?: string; employee?: { role?: string | null } | null }): boolean {
  if (gate.via === "schedule" || gate.via === "secret") return true;
  if (gate.via === "employee") {
    const role = gate.employee?.role;
    return role === "admin" || role === "super_admin";
  }
  return false;
}

export interface ArchiveResult {
  data: unknown;
  error: { message: string } | null;
}

export interface ArchiveChain extends PromiseLike<ArchiveResult> {
  select(columns: string): ArchiveChain;
  eq(column: string, value: unknown): ArchiveChain;
  in(column: string, values: readonly unknown[]): ArchiveChain;
  is(column: string, value: null): ArchiveChain;
  order(column: string): ArchiveChain;
  range(from: number, to: number): ArchiveChain;
}

export interface ArchiveClient {
  from(table: string): {
    select(columns: string): ArchiveChain;
    update(patch: Record<string, unknown>): ArchiveChain;
  };
}

export interface ArchiveRun {
  archived: number;
  boards: number;
  skipped?: boolean;
  reason?: string;
}

const WRITE_SIZE = 100;

function notReady(error: { message: string } | null): boolean {
  return !!error && /archived_at|auto_archive_done|archive_reason/i.test(error.message);
}

/**
 * Read the opted-in boards and stamp archived_at on their Complete cards.
 * Idempotent: the update also requires archived_at is null and the card is
 * still in a Complete column, so a card moved out between the read and the
 * write is left where the person put it.
 */
export async function runCompleteArchive(
  sb: ArchiveClient,
  opts: { now?: string; pageSize?: number } = {},
): Promise<ArchiveRun> {
  const now = opts.now ?? new Date().toISOString();
  const pageSize = opts.pageSize ?? 1000;

  const boardsRes = await sb.from("hq_task_boards")
    .select("id,auto_archive_done,archived")
    .eq("auto_archive_done", true)
    .eq("archived", false);
  if (boardsRes.error) {
    if (notReady(boardsRes.error)) return { archived: 0, boards: 0, skipped: true, reason: "not_ready" };
    throw new Error(boardsRes.error.message);
  }
  const boards = (boardsRes.data ?? []) as ArchiveBoard[];
  if (boards.length === 0) return { archived: 0, boards: 0 };

  const columnsRes = await sb.from("hq_task_columns")
    .select("id,board_id,is_done,kind")
    .in("board_id", boards.map(b => b.id))
    .eq("kind", "complete");
  if (columnsRes.error) throw new Error(columnsRes.error.message);
  const columns = (columnsRes.data ?? []) as ArchiveColumn[];
  if (columns.length === 0) return { archived: 0, boards: boards.length };

  const columnIds = columns.map(c => c.id);
  const cards: ArchiveCard[] = [];
  for (let from = 0; ; from += pageSize) {
    const page = await sb.from("hq_tasks")
      .select("id,board_id,column_id,archived_at")
      .in("column_id", columnIds)
      .is("archived_at", null)
      .order("id")
      .range(from, from + pageSize - 1);
    if (page.error) {
      if (notReady(page.error)) return { archived: 0, boards: boards.length, skipped: true, reason: "not_ready" };
      throw new Error(page.error.message);
    }
    const rows = (page.data ?? []) as ArchiveCard[];
    cards.push(...rows);
    if (rows.length < pageSize) break;
  }

  const chosen = cardsToArchive(boards, columns, cards);
  if (chosen.length === 0) return { archived: 0, boards: boards.length };

  const completeIds = [...new Set(chosen.map(c => c.column_id))];
  let archived = 0;
  for (const ids of chunk(chosen.map(c => c.id), WRITE_SIZE)) {
    const written = await stamp(sb, ids, completeIds, { archived_at: now, archive_reason: "auto_complete" });
    if (written.error) throw new Error(written.error.message);
    const rows = written.data;
    archived += Array.isArray(rows) ? rows.length : 0;
  }
  return { archived, boards: boards.length };
}

/** Sunday stamp. If archive_reason is not in the database yet, stamp the time only. */
async function stamp(
  sb: ArchiveClient,
  ids: string[],
  completeIds: string[],
  patch: { archived_at: string; archive_reason?: ArchiveReason },
): Promise<ArchiveResult> {
  const written = await sb.from("hq_tasks")
    .update(patch)
    .in("id", ids)
    .in("column_id", completeIds)
    .is("archived_at", null)
    .select("id");
  if (written.error && /archive_reason/i.test(written.error.message)) {
    return sb.from("hq_tasks")
      .update({ archived_at: patch.archived_at })
      .in("id", ids)
      .in("column_id", completeIds)
      .is("archived_at", null)
      .select("id");
  }
  return written;
}
